import React, { useState, useEffect, useCallback } from "react";

// Material UI
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Collapse, Typography } from "@mui/material";

// Material UI Icons
import { ExpandMore } from "@mui/icons-material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

// Menu Data
import navdata from "../LayoutMenuData";
import SimpleBar from "simplebar-react";

// Import Logo
import logosm from "../../assets/images/logo-sm.png";
import logodark from "../../assets/images/logo-dark.png";
import logolight from "../../assets/images/logo-light.png";

// Import Header
import Header from "../header";
import { useTheme } from "@emotion/react";
import { Link } from "react-router-dom";
import Footer from "../footer";
import useStyles, { Colortheme } from "../../pages/comman";

import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflow: "hidden",
  "& .MuiListItem-root:hover": {
    "& .MuiListItemText-root .MuiTypography-root": {
      color: theme.palette.mode === "light" ? "#609fff" : "#fff",
    },

    "& .MuiListItemIcon-root": {
      color: theme.palette.mode === "light" ? "#609fff" : "#fff",
    },
  },
  "& .MuiCollapse-root .MuiButtonBase-root:hover": {
    "& .MuiListItemText-root .MuiTypography-root": {
      color: theme.palette.mode === "light" ? "#609fff" : "#fff",
    },

    "& .MuiListItemIcon-root": {
      color: theme.palette.mode === "light" ? "#609fff" : "#fff",
    },
  },
  "& .MuiListItemText-root .MuiTypography-root": {
    color: theme.palette.mode === "light" ? "#6d7080" : "#a2aac4",
  },
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflow: "visible",
  position: "absolute",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
  "& .MuiCollapse-root .MuiButtonBase-root .MuiListItemText-root .MuiTypography-root":
    {
      color: theme.palette.mode === "light" ? "#6d7080" : "#a2aac4",
    },
  "& .MuiCollapse-root .MuiButtonBase-root .MuiListItemText-root .MuiTypography-root:hover":
    {
      color: theme.palette.mode === "light" ? "#609fff" : "#fff",
    },
  "& .listitem:hover": {
    "& .css-1cvak6n-MuiListItemIcon-root": {
      color: "#fff ", //icons color white
    },
    ".hedertext > .css-pb8n2w-MuiTypography-root": {
      color: "#fff",
    },
    ".hedericons": {
      //Authentication Icons
      display: "block",
      position: "absolute",
      left: "91%",
      color: "#fff",
    },
    ".listitemicons": {
      // Sign in  icons
      position: "absolute",
      left: "90%",
    },
    "> .MuiList-root": {
      width: "calc(200px + 70px )",
      backgroundColor: theme.palette.mode === "light" ? "#438eff" : "#1c2932",
    },
    "> .MuiButtonBase-root": {
      width: "calc(200px + 70px )",
      display: "flex",
      justifyContent: "start",
      backgroundColor: theme.palette.mode === "light" ? "#438eff" : "#1c2932",
      "& .MuiListItemText-root": {
        Border: "1px solid #fff",
      },
    },
    "> .MuiCollapse-root": {
      visibility: "visible",
      overflow: "visible",
      backgroundColor: theme.palette.mode === "light" ? "#fff" : "#1c2932",
      width: "calc(200px + 16px )",
      position: "absolute",
      left: "54px",
      boxShadow: "0 2px 4px rgba(15,34,58,.12)",
      height: "auto !important",
      ".listitemtext > .css-pb8n2w-MuiTypography-root": {
        color: "#7c7f90",
      },

      "& .MuiCollapse-root": {
        visibility: "hidden",
        overflow: "hidden",
      },

      // nested
      "& .MuiButtonBase-root:hover": {
        ".listitemicons": {
          color: "#438eff",
        },

        ".listitemtext >  .css-pb8n2w-MuiTypography-root": {
          color: theme.palette.mode === "light" ? "#438eff" : "#fff", // Dropdown Text color
        },

        "& .MuiCollapse-root": {
          visibility: "visible",
          overflow: "visible",
        },
      },
    },
  },

  "& .listitemnested:hover": {
    "& .MuiList-root": {
      width: "200px",
      backgroundColor: theme.palette.mode === "light" ? "#fff" : "#1c2932",
      boxShadow: "0 2px 4px rgba(15,34,58,.12)",
      position: "absolute",
      left: "100%",
    },
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: 1100,
  // theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    zIndex: 1100,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const VerticalLayout = (props: any) => {
  const theme: any = useTheme();
  const navData = navdata().props.children;
  const [open, setOpen] = useState(
    document.documentElement.clientWidth < 1025 ? false : true
  );
  const {
    sidebarBg,
    sidebarLink,
    sidebarLinkHover,
    sidebarLinkHoverFill,
    sidebarFontSize,
    inherit,
  } = Colortheme();

  //Fist Authentication Sidebar
  const StyledSidebarDropdownButton = styled(ListItemButton)({
    textDecoration: "none",
    color: sidebarLink,
    transition: "all 0.5s ease",

    "> .css-vkkvtg-MuiButtonBase-root-MuiListItemButton-root": {
      minHeight: 0,
      color: sidebarLink,
      padding: "5px 12px",
      borderRadius: "0.25rem",
    },
    ".css-pb8n2w-MuiTypography-root": {
      color: open ? "#7c7f90" : "#fff",
    },
    ".css-ccvmta-MuiListItemIcon-root": {
      marginRight: "10px",
      ".icons": {
        transition: "all 0.5s ease",
        color: sidebarLink,
      },
    },

    "&:hover": {
      backgroundColor: "transparent",
      ".css-wa5fuu-MuiListItemIcon-root": {
        ".icons": {
          color: sidebarLink,
          fill: sidebarLinkHoverFill,
        },
      },
      ".css-ccvmta-MuiListItemIcon-root": {
        ".icons": {
          color: sidebarLinkHover,
        },
      },
    },
  });

  //Link styles
  const StyledRouterList = styled(List)({
    // backgroundColor:'red !imporatnt',
    "& .sub-nav-active": {
      "& .css-11jc72g-MuiListItemIcon-root, & .MuiTypography-root, & .css-14p4906-MuiSvgIcon-root, & .css-ipphq6-MuiSvgIcon-root, & .css-fmmsr-MuiSvgIcon-root, & .css-1fhj5s2-MuiListItemIcon-root":
        {
          color:
            theme.palette.mode === "light"
              ? open
                ? "#5498fe !important"
                : "#fff !important"
              : "#fff !important",
        },
      "& .css-1cvak6n-MuiListItemIcon-root": {
        color: theme.palette.mode === "light" ? "#5498fe " : "#fff !important",
      },
    },
  });

  //Link styles
  const StyledRouterLink = styled(Link)({
    textDecoration: "none",
    color: sidebarLink,
    transition: "all 0.5s ease",

    "> div": {
      minHeight: 0,
      color: sidebarLink,
      padding: "8px 20px",
      borderRadius: "0.25rem",
    },
    ".css-pb8n2w-MuiTypography-root": {
      fontSize: sidebarFontSize,
      color: open ? "#7c7f90" : "#fff",
    },
    ".css-ccvmta-MuiListItemIcon-root": {
      marginRight: "10px",
      ".icons": {
        transition: "all 0.5s ease",
      },
    },
    "& .css-1uwk7d6-MuiButtonBase-root-MuiListItemButton-root:hover": {
      backgroundColor: "transparent",
    },
    "&:hover": {
      ".css-1dugtav-MuiListItemIcon-root": {
        ".icons": {
          transition: "all 0.5s ease",
          color: "#438eff",
        },
      },
      ".css-10c7cvy-MuiListItemText-root": {
        ".icons": {
          transition: "all 0.5s ease",
          color: "#438eff",
        },
      },
    },
    "&.nav-active": {
      "& .css-11jc72g-MuiListItemIcon-root, & .MuiTypography-root, & .css-1fhj5s2-MuiListItemIcon-root":
        {
          color:
            theme.palette.mode === "light"
              ? open
                ? "#5498fe !important"
                : "#fff !important"
              : "#fff !important",
        },
      "& .css-1cvak6n-MuiListItemIcon-root": {
        color: theme.palette.mode === "light" ? "#5498fe " : "#fff !important",
      },
    },
  });

  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  if (document.documentElement.clientWidth < 1025) {
    if (open) {
      document.body.classList.add("hidden");
    } else {
      document.body.classList.remove("hidden");
    }
  } else {
    document.body.classList.remove("hidden");
  }

  const resizeSidebarMenu = useCallback(() => {
    var windowSize = document.documentElement.clientWidth;
    if (windowSize >= 900) {
      setOpen(true);
    } else if (windowSize < 900) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", resizeSidebarMenu, true);
  }, [resizeSidebarMenu]);

  const { cardbg, sidebarpage } = Colortheme();

  const classes = useStyles();

  useEffect(() => {
    const path = props.router.location.pathname;
    window.scrollTo({ top: 0, behavior: "smooth" });
    const pathName = process.env.PUBLIC_URL + path;
    const ul = document.getElementById("navbar-nav") as HTMLElement;
    const items: any = ul.getElementsByTagName("a");
    let itemsArray = [...items]; // converts NodeList to Array

    removeActivation();
    let matchingMenuItem = itemsArray.find((x) => {
      return x.pathname === pathName;
    });

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  });

  const removeActivation = () => {
    const ul = document.getElementById("navbar-nav") as HTMLElement;
    const items: any = ul.getElementsByClassName("nav-active");
    let itemsArray = [...items];

    itemsArray.forEach((item: any) => {
      if (item.classList.contains("nav-active")) {
        item.classList.remove("nav-active");
        item.classList.remove("sub-nav-active");
      }
    });
  };

  function activateParentDropdown(item: any) {
    item.classList.add("nav-active");

    let parentDiv = item.closest(".parent-div");
    if (parentDiv) {
      // Set aria-expand attribute to true (remaining logic needs to be implemented)
      const previousSibling = parentDiv.previousElementSibling;
      if (previousSibling) {
        previousSibling.classList.add("sub-nav-active");
        let parentElement = previousSibling.closest(".parent-div");
        if (parentElement) {
          parentElement.previousElementSibling?.classList.add("sub-nav-active");
        }
      }
    }
  }

  return (
    <Box sx={{ ...(!open && { position: "relative" }) }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        style={{ backgroundColor: sidebarBg }}
        elevation={0}
      >
        <Header
          handleDrawerToggle={handleDrawerToggle}
          open={open}
          sx={{ display: { xs: "none", md: "block" } }}
        >
          <Typography
            sx={{
              marginTop: "5px",
              marginRight: open ? 0 : 3,
              ...(open && { display: "none" }),
              [theme.breakpoints.down("sm")]: {
                display: "none",
              },
            }}
          >
            <img src={logosm} alt="rudrix" height="22" />
          </Typography>
        </Header>
      </AppBar>
      {/* sidebar */}
      <Drawer
        variant="permanent"
        elevation={0}
        open={open}
        className={classes.sidebar}
      >
        <DrawerHeader sx={{ backgroundColor: cardbg, marginTop: "26px" }}>
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: open ? "block" : "none",
              padding: "4px 0",
            }}
          >
            <a
              href="/components"
              className="logo logo-dark"
              style={{ height: "100%", width: "100%" }}
            >
              <span
                className="logo-lg"
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={theme.palette.mode === "dark" ? logolight : logodark}
                  alt="rudrix"
                  height="25"
                />
              </span>
            </a>
          </Box>
        </DrawerHeader>
        {/* md sidebar */}
        {open ? (
          <>
            <SimpleBar style={{ height: "calc(100% - 82px)" }}>
              <StyledRouterList
                sx={{ backgroundColor: cardbg }}
                id="navbar-nav"
              >
                {navData.map((item: any, key: number) => (
                  <React.Fragment key={key}>
                    {item["isHeader"] ? (
                      <ListItem disablePadding sx={{ display: "block" }}>
                        {/* menu */}
                        <ListItemButton
                          sx={{
                            minHeight: 30,
                            justifyContent: open ? "initial" : "center",
                            px: "20px",
                            py: "6px",
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: open ? 0 : "auto",
                              justifyContent: "center",
                              m: 0,
                              marginRight: "0px",
                            }}
                          >
                            <MoreHorizIcon
                              sx={{
                                display: open ? "none" : "block",
                                fontSize: "15px",
                                color: inherit,
                              }}
                            />
                          </ListItemIcon>
                          <Typography
                            sx={{
                              textTransform: "uppercase",
                              fontWeight: 600,
                              color: sidebarpage,
                              fontSize: "11px",
                              display: open ? "block" : "none",
                              m: 0,
                            }}
                          >
                            {props.t(item.label)}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ) : item.subItems ? (
                      // dropdown
                      <React.Fragment>
                        <ListItem
                          disablePadding
                          className="lgtextColor"
                          sx={{ display: "block" }}
                        >
                          <StyledSidebarDropdownButton
                            sx={{
                              minHeight: 48,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                              marginRight: "0px",
                            }}
                            onClick={item.click}
                          >
                            <ListItemIcon
                              sx={{
                                width: "16px",
                                height: "16px",
                                marginTop: "-6px",
                                minWidth: 0,
                                justifyContent: "center",
                                marginRight: open ? "12px" : "0",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#7c7f90"
                                    : "inherit",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={props.t(item.label)}
                              sx={{
                                opacity: open ? 1 : 0,
                                fontSize: "6px",
                                color: inherit,
                              }}
                            />
                            {open ? (
                              item.stateVariables ? (
                                <ExpandMore
                                  sx={{
                                    width: "16px",
                                    height: "16px",
                                    fontSize: "16px",
                                    color: inherit,
                                  }}
                                />
                              ) : (
                                <ChevronRightIcon
                                  sx={{
                                    width: "16px",
                                    marginTop: "-6px",
                                    height: "16px",
                                    fontSize: "16px",
                                    color: inherit,
                                  }}
                                />
                              )
                            ) : (
                              ""
                            )}
                          </StyledSidebarDropdownButton>
                        </ListItem>
                        <Collapse
                          in={open && item.stateVariables}
                          className="parent-div"
                        >
                          <StyledRouterList disablePadding>
                            {item.subItems.map((item: any, key: number) =>
                              item.isChildItem ? (
                                <React.Fragment key={key}>
                                  <ListItemButton sx={{ pl: 6 }}>
                                    <ListItemText
                                      sx={{ fontSize: "6px", color: "inherit" }}
                                      primary={props.t(item.label)}
                                      onClick={item.click}
                                    />
                                    {open ? (
                                      item.stateVariables ? (
                                        <ExpandMore
                                          sx={{
                                            fontSize: "14px",
                                            color: inherit,
                                          }}
                                        />
                                      ) : (
                                        <ChevronRightIcon
                                          sx={{
                                            fontSize: "14px",
                                            color: inherit,
                                          }}
                                        />
                                      )
                                    ) : (
                                      ""
                                    )}
                                  </ListItemButton>
                                  <Collapse
                                    in={item.stateVariables}
                                    className="parent-div"
                                  >
                                    <List
                                      component="div"
                                      disablePadding
                                      sx={{ marginLeft: " 42px" }}
                                    >
                                      {item.childItems.map(
                                        (item: any, key: number) =>
                                          item.isChildItem ? (
                                            <React.Fragment key={key}>
                                              <ListItemButton sx={{ pl: 7 }}>
                                                {" "}
                                                <ListItemText
                                                  sx={{
                                                    fontSize: "14px",
                                                    color: inherit,
                                                  }}
                                                  primary={props.t(item.label)}
                                                  onClick={item.click}
                                                />
                                                {open ? (
                                                  item.stateVariables ? (
                                                    <ExpandMore />
                                                  ) : (
                                                    <ChevronRightIcon
                                                      sx={{
                                                        fontSize: "14px",
                                                        color: inherit,
                                                      }}
                                                    />
                                                  )
                                                ) : (
                                                  ""
                                                )}
                                              </ListItemButton>
                                              <Collapse
                                                in={item.stateVariables}
                                              >
                                                <List
                                                  component="div"
                                                  disablePadding
                                                >
                                                  {item.childItems.map(
                                                    (
                                                      item: any,
                                                      key: number
                                                    ) => (
                                                      <ListItemButton
                                                        sx={{ pl: 8 }}
                                                        key={key}
                                                      >
                                                        {" "}
                                                        <ListItemText
                                                          sx={{
                                                            fontSize: "14px",
                                                          }}
                                                          primary={props.t(
                                                            item.label
                                                          )}
                                                        />{" "}
                                                      </ListItemButton>
                                                    )
                                                  )}
                                                </List>
                                              </Collapse>
                                            </React.Fragment>
                                          ) : (
                                            <StyledRouterLink
                                              to={item.link}
                                              key={key}
                                            >
                                              <ListItemButton
                                                sx={{ pl: "30px" }}
                                                key={key}
                                              >
                                                {" "}
                                                <ListItemText
                                                  sx={{ fontSize: "14px" }}
                                                  primary={props.t(item.label)}
                                                />{" "}
                                              </ListItemButton>
                                            </StyledRouterLink>
                                          )
                                      )}
                                    </List>
                                  </Collapse>
                                </React.Fragment>
                              ) : (
                                <StyledRouterLink to={item.link} key={key}>
                                  <ListItemButton sx={{ pl: 6 }}>
                                    <ListItemText
                                      sx={{ marginLeft: "24px" }}
                                      primary={props.t(item.label)}
                                    />
                                  </ListItemButton>
                                </StyledRouterLink>
                              )
                            )}
                          </StyledRouterList>
                        </Collapse>
                      </React.Fragment>
                    ) : (
                      <ListItem disablePadding sx={{ display: "block" }}>
                        <StyledRouterLink to={item.link}>
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                              color: inherit,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                width: "16px",
                                height: "16px",
                                marginTop: "-6px",
                                minWidth: 0,
                                justifyContent: "center",
                                marginRight: open ? "12px" : "0",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#7c7f90"
                                    : "inherit",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={props.t(item.label)}
                              sx={{ opacity: open ? 1 : 0, color: inherit }}
                              onClick={item.click}
                            />
                          </ListItemButton>
                        </StyledRouterLink>
                      </ListItem>
                    )}
                  </React.Fragment>
                ))}
              </StyledRouterList>
            </SimpleBar>
          </>
        ) : (
          <>
            <StyledRouterList sx={{ backgroundColor: cardbg }} id="navbar-nav">
              {navData.map((item: any, key: number) => (
                <React.Fragment key={key}>
                  {item["isHeader"] ? (
                    <ListItem disablePadding sx={{ display: "block" }}>
                      {/* menu */}
                      <ListItemButton
                        sx={{
                          minHeight: 30,
                          justifyContent: open ? "initial" : "center",
                          px: "20px",
                          py: "6px",
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 0 : "auto",
                            justifyContent: "center",
                            m: 0,
                            marginRight: "0px",
                          }}
                        >
                          <MoreHorizIcon
                            sx={{
                              display: open ? "none" : "block",
                              fontSize: "15px",
                              color: inherit,
                            }}
                          />
                        </ListItemIcon>
                        <Typography
                          sx={{
                            textTransform: "uppercase",
                            fontWeight: 600,
                            color: sidebarpage,
                            fontSize: "11px",
                            display: open ? "block" : "none",
                            m: 0,
                          }}
                        >
                          {props.t(item.label)}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  ) : item.subItems ? (
                    // dropdown
                    <React.Fragment>
                      <ListItem
                        disablePadding
                        className="listitem"
                        sx={{ display: "block" }}
                      >
                        <StyledSidebarDropdownButton
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                            marginRight: "0px",
                          }}
                          onClick={item.click}
                        >
                          <ListItemIcon
                            sx={{
                              width: "16px",
                              height: "16px",
                              marginTop: "-6px",
                              minWidth: 0,
                              justifyContent: "center",
                              marginRight: open ? "12px" : "0",
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={props.t(item.label)}
                            sx={{ opacity: 0, fontSize: "6px" }}
                            className="Sidebarmain"
                          />
                          {open ? (
                            item.stateVariables ? (
                              <ExpandMore
                                sx={{
                                  width: "16px",
                                  height: "16px",
                                  fontSize: "16px",
                                  color: inherit,
                                }}
                              />
                            ) : (
                              <ChevronRightIcon
                                sx={{
                                  width: "16px",
                                  marginTop: "-6px",
                                  height: "16px",
                                  fontSize: "16px",
                                  color: inherit,
                                }}
                              />
                            )
                          ) : (
                            <ExpandMore
                              className="hedericons"
                              sx={{
                                width: "16px",
                                height: "16px",
                                fontSize: "16px",
                                color: inherit,
                                display: "none",
                              }}
                            />
                          )}
                        </StyledSidebarDropdownButton>
                        <Collapse
                          in={open && item.stateVariables}
                          className="parent-div"
                        >
                          <List component="div" disablePadding>
                            {item.subItems.map((item: any, key: number) =>
                              item.isChildItem ? (
                                <React.Fragment key={key}>
                                  <ListItemButton
                                    sx={{ pl: 6 }}
                                    className="listitemnested"
                                  >
                                    <ListItemText
                                      sx={{ fontSize: "14px", color: inherit }}
                                      className="listitemtext"
                                      primary={props.t(item.label)}
                                      onClick={item.click}
                                    />
                                    {open ? (
                                      item.stateVariables ? (
                                        <ExpandMore
                                          sx={{
                                            fontSize: "14px",
                                            color: inherit,
                                          }}
                                        />
                                      ) : (
                                        <ChevronRightIcon
                                          sx={{
                                            fontSize: "14px",
                                            color: inherit,
                                          }}
                                        />
                                      )
                                    ) : (
                                      <ChevronRightIcon
                                        className="listitemicons"
                                        sx={{
                                          fontSize: "14px",
                                          color: inherit,
                                        }}
                                      />
                                    )}
                                    <Collapse
                                      in={item.stateVariables}
                                      className="parent-div"
                                    >
                                      <List component="div" disablePadding>
                                        {item.childItems.map(
                                          (item: any, key: number) =>
                                            item.isChildItem ? (
                                              <React.Fragment key={key}>
                                                <ListItemButton sx={{ pl: 7 }}>
                                                  {" "}
                                                  <ListItemText
                                                    sx={{
                                                      fontSize: "14px",
                                                      color: inherit,
                                                    }}
                                                    primary={props.t(
                                                      item.label
                                                    )}
                                                    onClick={item.click}
                                                  />
                                                  {open ? (
                                                    item.stateVariables ? (
                                                      <ExpandMore />
                                                    ) : (
                                                      <ChevronRightIcon
                                                        sx={{
                                                          fontSize: "14px",
                                                          color: inherit,
                                                        }}
                                                      />
                                                    )
                                                  ) : (
                                                    ""
                                                  )}
                                                </ListItemButton>
                                                <Collapse
                                                  in={item.stateVariables}
                                                >
                                                  <List
                                                    component="div"
                                                    disablePadding
                                                  >
                                                    {item.childItems.map(
                                                      (
                                                        item: any,
                                                        key: number
                                                      ) => (
                                                        <ListItemButton
                                                          sx={{ pl: 8 }}
                                                          key={key}
                                                        >
                                                          {" "}
                                                          <ListItemText
                                                            sx={{
                                                              fontSize: "14px",
                                                            }}
                                                            primary={props.t(
                                                              item.label
                                                            )}
                                                          />{" "}
                                                        </ListItemButton>
                                                      )
                                                    )}
                                                  </List>
                                                </Collapse>
                                              </React.Fragment>
                                            ) : (
                                              <StyledRouterLink
                                                to={item.link}
                                                key={key}
                                              >
                                                <ListItemButton
                                                  sx={{ pl: "30px" }}
                                                  key={key}
                                                >
                                                  {" "}
                                                  <ListItemText
                                                    sx={{ fontSize: "14px" }}
                                                    primary={props.t(
                                                      item.label
                                                    )}
                                                  />{" "}
                                                </ListItemButton>
                                              </StyledRouterLink>
                                            )
                                        )}
                                      </List>
                                    </Collapse>
                                  </ListItemButton>
                                </React.Fragment>
                              ) : (
                                <StyledRouterLink to={item.link} key={key}>
                                  <ListItemButton sx={{ pl: 6 }}>
                                    <ListItemText
                                      sx={{ marginLeft: "24px" }}
                                      primary={props.t(item.label)}
                                    />
                                  </ListItemButton>
                                </StyledRouterLink>
                              )
                            )}
                          </List>
                        </Collapse>
                      </ListItem>
                    </React.Fragment>
                  ) : (
                    <ListItem
                      className="listitem"
                      disablePadding
                      sx={{ display: "block" }}
                    >
                      <List component="div" disablePadding>
                        <StyledRouterLink to={item.link}>
                          <ListItemButton
                            sx={{
                              minHeight: 48,
                              justifyContent: open ? "initial" : "center",
                              px: 2.5,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                width: "16px",
                                height: "16px",
                                marginTop: "-6px",
                                minWidth: 0,
                                justifyContent: "center",
                                marginRight: open ? "12px" : "0",
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              className="hedertext"
                              primary={props.t(item.label)}
                              sx={{ opacity: 0 }}
                              onClick={item.click}
                            />
                          </ListItemButton>
                        </StyledRouterLink>
                      </List>
                    </ListItem>
                  )}
                </React.Fragment>
              ))}
            </StyledRouterList>
          </>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          marginTop: "26px",
          marginLeft: {
            xs: "0",
            lg: open ? "240px" : "64px",
            position: "relative",
            minHeight: "97vh",
          },
          paddingBottom: "80px",
        }}
      >
        <DrawerHeader sx={{ zIndex: "-1" }} />
        {props.children}
        {/* <Footer open={open} /> */}
      </Box>

      {open && document.documentElement.clientWidth < 1025 && (
        <Box
          sx={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(20,24,33,.35)",
            zIndex: "1200",
          }}
          onClick={() => setOpen(false)}
        ></Box>
      )}
    </Box>
  );
};

export default withRouter(withTranslation()(VerticalLayout));
